/* ================================================================
   BRITTEN CUSTOM: Dress match — "Brides in a dress like yours"
   ================================================================
   Vanilla IIFE, no framework, no build step. Mount: #bdm (snippets/
   britten-dress-match.liquid). This file is the PAGE half: every element,
   chip, result card and lightbox on the Real Brides block. The ENGINE half
   (reading a photo, the bride index, matching, remembering the dress) lives
   in assets/britten-dress-match-core.js (window.BrittenDressMatchCore),
   loaded just before this file so the Dress Finder page can share it. If
   the core is missing the block simply stays hidden. Matching engine:
   window.BrittenDressMatch (assets/britten-dress-match-score.js, written
   by the MarketingOS publish — lazy-loaded by the core). Bride index:
   assets/britten-dress-match.json (same publish). See MarketingOS
   design/DRESS-MATCH.md.

   Flow: choose/drop a photo -> instant local preview -> the core downsizes
   it to a 1200px JPEG in the browser (also strips EXIF/location) and POSTs
   it to the MarketingOS Netlify function -> tags come back -> matched
   against the index in the browser (no further network calls) -> results
   grouped by veil, or closest-first. Every string this file writes into
   the page comes from window.BrittenI18n (britten.js.rb.dm_*), rendered by
   snippets/britten-i18n-js.liquid — never a bare literal, so a translated
   store never shows English here.

   Hand-over from the Dress Finder: the dress the customer described there
   is kept in sessionStorage by the core (attributes only, never a photo).
   Arriving with ?dress=1 opens this block straight into results for that
   dress — no upload, no thumbnail — and scrolls to it.

   Archive hook (added 2026-09-29, backwards compatible): two CustomEvents
   let a host page use this block's answer without its markup. Nothing else
   listens for or dispatches either (the Dress Finder runs on the core, not on
   this file), so every page that renders this block is unchanged; the Real
   Brides archive (assets/britten-archive-weddings.js) hides this block's own
   results and renders its own from them.
     bdm:results  dispatched on #bdm (bubbles) every time the answer changes:
                  after the endpoint answers, after any chip edit, and when
                  there is no answer (reason set). detail:
                    { tags:    the raw tags now in use, or null (no answer),
                      chips:   [{ field, label }] the primary chips this block
                               shows for a known dress (silhouette, neckline...),
                      results: [{ id, slug, href, band, score, matched, photo,
                               veil }] in similarity order (the engine's own
                               rank), [] when there is no real match,
                      reason:  only when there is no answer: not_a_dress |
                               unclear | too_large | bad_image | busy | paused |
                               unavailable | timeout }
                  slug is href without "/pages/" ('' if no feature page);
                  band is very_close | close | similar; matched is the list of
                  fields both dresses share (heaviest first, no "none"/"no"
                  answers, no extras); photo is { u, w, h } (this block's
                  best photo for the bride: its first portrait photo, i.e. a
                  full-length shot, else its first); veil is { h, t } (product
                  handle, title) of the veil in that photo, or null.
     bdm:edit     listened for on #bdm: detail { field, anchor } opens that
                  field's editor popover under `anchor`, exactly as tapping the
                  chip would (the archive's own chips call it).

   CHANGELOG:
   - 2026-10-15: crops aim at the bride (fp, marketing_131); no fp = old crop. The
     result cards set object-position from the photo's fp, and the bdm:results
     hook now carries fp on each photo so the archive's dress cards can aim too.
   - 2026-09-29: round 4 upload results — the bdm:results / bdm:edit hook
     above (announceResults(), primaryChips(), bestPhotoOf(),
     resultsForHook(), the bdm:edit listener; announce calls where the answer
     is a failure, where the block stands down, and where rankAndRender()
     finishes). Nothing rendered, fetched or remembered here changed.
   ================================================================ */
(function () {
  'use strict';

  // === CONFIGURATION ===
  var FEATURE_ENABLED = true;
  var DEBUG_MODE = false;

  function log(msg) {
    if (DEBUG_MODE && window.console) console.log('[Britten Dress Match] ' + msg);
  }

  if (!FEATURE_ENABLED) return;

  var root = document.getElementById('bdm');
  if (!root) return;

  // The engine half. Without it the block has nothing to run on, so it
  // stays `hidden` (as the markup ships) and nothing throws.
  var Core = window.BrittenDressMatchCore;
  if (!Core) { log('core script missing — block stays hidden'); return; }

  // ---- config (from the mount element's data attributes) ----
  var core = Core.create({
    indexUrl: root.getAttribute('data-index-url') || '',
    scoreUrl: root.getAttribute('data-score-url') || '',
    endpoint: root.getAttribute('data-endpoint') || '',
    turnstileSitekey: root.getAttribute('data-turnstile-sitekey') || '',
    debug: DEBUG_MODE
  });
  var READING_INTERVAL_MS = 1400;
  // Arriving from the Dress Finder's "See all in Real Brides" link.
  var RECALL_REQUESTED = /[?&]dress=1(?:[&#]|$)/.test(window.location.search);

  // Matching fields (design/DRESS-MATCH.md "Vocabulary"), same order the
  // scoring engine's profileDescription() displays known ones in — the
  // describe-mode requirement ("silhouette/neckline/sleeves prompted
  // first") falls out of this order for free. `is_wedding_dress`,
  // `coverage` and `view` describe the PHOTO, not the garment, and never
  // appear here — the engine excludes them from matching too.
  var DRESS_FIELDS = ['silhouette', 'neckline', 'sleeves', 'sleeve_fabric',
    'lace', 'back', 'buttons', 'train', 'fabric', 'embellishment', 'colour', 'slit'];

  // Primary chips show first; everything else (plus any "negative" value —
  // no buttons, no train...) sits behind the quiet "More details" disclosure.
  // The index carries its own chip_secondary/chip_hide_values once the vocab
  // (pipeline/dress_vocab.json) grows them — these are only the fallback for
  // an index built before that, so the block never breaks on an older one.
  var DEFAULT_PRIMARY_FIELDS = ['silhouette', 'neckline', 'sleeves', 'lace', 'back', 'train', 'fabric'];
  var DEFAULT_HIDE_VALUES = {
    buttons: ['no'], slit: ['no'], embellishment: ['none'], lace: ['none'],
    train: ['none'], sleeve_fabric: ['na']
  };

  // ---- i18n (window.BrittenI18n rendered in <head> by
  // snippets/britten-i18n-js.liquid — see that file's header for the
  // receiver-naming convention scripts/i18n-js-keys-check.py relies on). ----
  var i18n = window.BrittenI18n;
  var safeI18n = {
    t: function (key, vars) { return i18n ? i18n.t(key, vars) : ''; },
    n: function (key, count, vars) { return i18n ? i18n.n(key, count, vars) : ''; }
  };

  var FIELD_LABEL = {
    silhouette: safeI18n.t('js.rb.dm_field_silhouette'),
    neckline: safeI18n.t('js.rb.dm_field_neckline'),
    sleeves: safeI18n.t('js.rb.dm_field_sleeves'),
    sleeve_fabric: safeI18n.t('js.rb.dm_field_sleeve_fabric'),
    lace: safeI18n.t('js.rb.dm_field_lace'),
    back: safeI18n.t('js.rb.dm_field_back'),
    buttons: safeI18n.t('js.rb.dm_field_buttons'),
    train: safeI18n.t('js.rb.dm_field_train'),
    fabric: safeI18n.t('js.rb.dm_field_fabric'),
    embellishment: safeI18n.t('js.rb.dm_field_embellishment'),
    colour: safeI18n.t('js.rb.dm_field_colour'),
    slit: safeI18n.t('js.rb.dm_field_slit'),
    extras: safeI18n.t('js.rb.dm_field_extras')
  };
  var BAND_LABEL = {
    very_close: safeI18n.t('js.rb.dm_band_very_close'),
    close: safeI18n.t('js.rb.dm_band_close'),
    similar: safeI18n.t('js.rb.dm_band_similar')
  };
  var READING_LINES = [
    safeI18n.t('js.rb.dm_reading_1'),
    safeI18n.t('js.rb.dm_reading_2'),
    safeI18n.t('js.rb.dm_reading_3')
  ];

  // ---- DOM refs ----
  var invite = document.getElementById('bdm-invite');
  var session = document.getElementById('bdm-session');
  var drop = document.getElementById('bdm-drop');
  var fileInput = document.getElementById('bdm-file-input');
  var describeOpenBtn = document.getElementById('bdm-describe-open');
  var preview = document.getElementById('bdm-preview');
  var previewImg = document.getElementById('bdm-preview-img');
  var shimmer = document.getElementById('bdm-shimmer');
  var statusEl = document.getElementById('bdm-status');
  var bannerEl = document.getElementById('bdm-banner');
  var chipsEl = document.getElementById('bdm-chips');
  var editDetailsBtn = document.getElementById('bdm-edit-details');
  var moreToggleBtn = document.getElementById('bdm-more-toggle');
  var moreEl = document.getElementById('bdm-more');
  var startAgainBtn = document.getElementById('bdm-start-again');
  var resultsEl = document.getElementById('bdm-results');
  var countEl = document.getElementById('bdm-count');
  var viewGroupedBtn = document.getElementById('bdm-view-grouped');
  var viewClosestBtn = document.getElementById('bdm-view-closest');
  var panelEl = document.getElementById('bdm-panel');
  var popEl = document.getElementById('bdm-pop');
  var lb = document.getElementById('bdm-lb');
  var lbTitle = document.getElementById('bdm-lb-title');
  var lbClose = document.getElementById('bdm-lb-close');
  var lbImg = document.getElementById('bdm-lb-img');
  var lbPrev = document.getElementById('bdm-lb-prev');
  var lbNext = document.getElementById('bdm-lb-next');
  var lbLinks = document.getElementById('bdm-lb-links');
  var lbCredit = document.getElementById('bdm-lb-credit');
  var unavailableEl = document.getElementById('bdm-unavailable');

  // The remembered dress's tags (attributes only), or null.
  function recalledTags() {
    var remembered = core.recall();
    return remembered ? remembered.tags : null;
  }

  // ---- state ----
  var state = {
    index: null,
    enginePromise: null,
    rawTags: null,
    ranked: null,
    view: 'grouped',
    readingTimer: null,
    readingIndex: 0,
    activeField: null,
    moreOpen: false,
    primaryExpanded: false,
    lastCardEl: null,
    lbList: [],
    lbIndex: 0,
    lbBride: null,
    lbVeil: null,
    // previewObjectUrl tracks the local preview blob: URL so it can be
    // released the moment it is replaced or no longer needed. (Which photo
    // is current, and aborting a superseded upload, is the core's job.)
    previewObjectUrl: null,
    // Set once and for all by showUnavailable() (the engine failed, or the
    // index has no brides yet) — once true, handleFile/enterDescribeMode
    // stand down rather than re-opening a session that can never do
    // anything, which would otherwise undo the graceful hide.
    unavailable: false,
    // The remembered dress a ?dress=1 arrival opens with, until it is used
    // (openFromRecall), and whether to scroll to the block once the results
    // for it have rendered.
    recalled: RECALL_REQUESTED ? recalledTags() : null,
    scrollWhenReady: false
  };

  // ---- analytics (no new vendors) — the core owns the event shape ----
  function dispatch(name, detail) { core.emit(name, detail); }

  // ---- archive hook (bdm:results) — see the header ----
  function announceResults(detail) {
    try { root.dispatchEvent(new CustomEvent('bdm:results', { bubbles: true, detail: detail })); } catch (e) { /* noop */ }
  }

  // ---- small helpers ----
  function cdnUrl(u, w) {
    if (!u) return u;
    if (!/^https?:\/\/cdn\.shopify\.com\//.test(u)) return u;
    var sep = u.indexOf('?') > -1 ? '&' : '?';
    return u + sep + 'width=' + w;
  }

  function veilFor(id) {
    return (state.index && state.index.veils && state.index.veils[id]) || null;
  }

  function veilNameForPhoto(photo) {
    if (!photo || photo.veil == null) return '';
    var v = veilFor(photo.veil);
    return v ? v.name : '';
  }

  function prefersReducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  // Once the answer lands, bring the chips + first result row into view —
  // most useful on mobile, where the invite card may have scrolled the
  // block some way up the page while the photo was read.
  function scrollAnswerIntoView() {
    if (typeof root.scrollIntoView !== 'function') return;
    requestAnimationFrame(function () {
      root.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    });
  }

  // ================================================================
  // Launch safety: the block starts `hidden` (snippets/britten-dress-match.
  // liquid) and must never appear before its data does. core.probeIndex()
  // is a CHEAP existence check only — HEAD, or (where HEAD isn't supported)
  // a 1-byte ranged GET — never the full index. A failure leaves the block
  // hidden, silently: nobody has seen it yet, so there is nothing to
  // apologise for. The real, full index/engine load stays exactly as lazy as
  // before (scroll-into-view or first interaction, see "Engine" below); this
  // check runs once, on idle, purely to decide whether to reveal at all.
  // ================================================================
  function revealBlock() {
    root.hidden = false;
    // A dress handed over from the Dress Finder opens the results at once,
    // wherever the block is on the page.
    if (state.recalled) { openFromRecall(); return; }
    // observeForEngine's IntersectionObserver was attached while this
    // element was display:none (nothing to observe yet), so a display:none
    // -> block transition isn't guaranteed to produce a fresh callback on
    // its own in every engine. getBoundingClientRect() forces the browser to
    // lay this element out right now (a synchronous reflow, but it happens
    // once, not in a loop) so this check doesn't depend on a compositor
    // frame ever arriving — unlike requestAnimationFrame, which some
    // headless/background-tab conditions never schedule. The observer stays
    // attached afterwards too, for the ordinary case where the block
    // reveals off-screen and the customer later scrolls to it.
    var r = root.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    if (r.top < vh + 200 && r.bottom > -200) ensureEngineLoaded();
  }
  function checkIndexExists() {
    core.probeIndex().then(function (exists) {
      if (exists) revealBlock();
      // no index yet, or a network error: stay hidden, silently
    });
  }
  function scheduleIndexCheck() {
    if ('requestIdleCallback' in window) requestIdleCallback(checkIndexExists, { timeout: 2000 });
    else setTimeout(checkIndexExists, 1);
  }

  // ================================================================
  // Engine: score script + index (the core loads them; this waits for the
  // block to scroll into view, or the first real interaction, to ask).
  // Turnstile and heic2any are lazy inside the core, never blocking.
  // ================================================================

  // Shows a calm, permanent notice in place of the whole block and hides
  // the upload/results UI — used when the index or scoring engine can't be
  // used at all, so the customer is never left looking at a photo picker
  // (or a mid-upload spinner) that can't go anywhere. Never throws.
  function showUnavailable(msg) {
    try {
      state.unavailable = true;
      stopReadingStatus();
      if (shimmer) shimmer.hidden = true;
      if (invite) invite.hidden = true;
      if (session) session.hidden = true;
      clearBanner();
      if (unavailableEl) {
        unavailableEl.textContent = msg || '';
        unavailableEl.hidden = false;
      }
      announceResults({ tags: null, chips: [], results: [], reason: 'unavailable' });
    } catch (e) { /* never throw out of a failure path */ }
  }

  function ensureEngineLoaded() {
    if (state.enginePromise) return state.enginePromise;
    state.enginePromise = core.loadEngine().then(function (engine) {
      state.index = engine.index;
      if (state.rawTags) { renderChips(); rankAndRender(); }
      if (state.scrollWhenReady) { state.scrollWhenReady = false; scrollAnswerIntoView(); }
    }, function (err) {
      // The index or the score script failed to load outright, or the index
      // has no brides yet: matching can never run this visit, so the whole
      // block stands down rather than leaving a blank/broken UI behind an
      // upload.
      log('engine unavailable: ' + (err && err.code));
      if (err && err.code === 'empty_index') {
        showUnavailable(safeI18n.t('js.rb.dm_state_no_brides_yet'));
      } else {
        showUnavailable(safeI18n.t('js.rb.dm_state_engine_unavailable'));
      }
    });
    return state.enginePromise;
  }

  function observeForEngine() {
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) { ensureEngineLoaded(); io.disconnect(); break; }
        }
      }, { rootMargin: '200px 0px' });
      io.observe(root);
    } else {
      ensureEngineLoaded();
    }
  }

  // ================================================================
  // Photo -> tags (the core does the work; this paints the outcome)
  // ================================================================
  // Starting a new photo (or "Start again") supersedes the one in flight
  // inside the core: its answer resolves 'superseded' and is dropped here
  // without touching the page, so the reading status a newer photo started
  // keeps running.
  function processAndSend(file) {
    ensureEngineLoaded();
    startReadingStatus();
    core.readPhoto(file, {
      onPreview: function (dataUrl) {
        revokePreviewObjectUrl();
        previewImg.src = dataUrl;
      },
      onStatus: function (code) {
        if (code === 'heic_converting') showStatus(safeI18n.t('js.rb.dm_heic_converting'));
      }
    }).then(handleReadResult);
  }

  function handleReadResult(res) {
    if (res.reason === 'superseded') return; // a newer photo (or Start again) owns the page now
    stopReadingStatus();
    shimmer.hidden = true;

    if (!res.ok) {
      var reason = res.reason;
      announceResults({ tags: null, chips: [], results: [], reason: reason || 'unavailable' });
      if (reason === 'not_a_dress' || reason === 'unclear') {
        showBanner(safeI18n.t('js.rb.dm_state_not_dress'));
        return;
      }
      if (reason === 'too_large') { showBanner(safeI18n.t('js.rb.dm_state_too_large')); return; }
      if (reason === 'bad_image') { showBanner(safeI18n.t('js.rb.dm_state_bad_image')); return; }
      // busy | paused | unavailable | timeout — never a dead end.
      showBanner(safeI18n.t('js.rb.dm_state_resting'));
      enterDescribeMode(true);
      return;
    }

    var tags = res.tags || {};
    if (res.coverage === 'none' || res.coverage === 'detail') {
      showBanner(safeI18n.t('js.rb.dm_state_low_coverage'));
    } else {
      clearBanner();
    }
    setStatusReady();
    dispatch('tags_ready', { fields: Object.keys(tags).length });
    setQueryTags(tags);
    scrollAnswerIntoView();
  }

  // ================================================================
  // Reading status (cycling aria-live line while we wait)
  // ================================================================
  function startReadingStatus() {
    state.readingIndex = 0;
    statusEl.textContent = READING_LINES[0] || '';
    stopReadingStatus();
    state.readingTimer = setInterval(function () {
      state.readingIndex = (state.readingIndex + 1) % READING_LINES.length;
      statusEl.textContent = READING_LINES[state.readingIndex] || '';
    }, READING_INTERVAL_MS);
  }
  function stopReadingStatus() {
    if (state.readingTimer) { clearInterval(state.readingTimer); state.readingTimer = null; }
  }
  function showStatus(msg) { statusEl.textContent = msg || ''; }
  function setStatusReady() { statusEl.textContent = safeI18n.t('js.rb.dm_tags_ready'); }

  function showBanner(msg) {
    bannerEl.textContent = msg || '';
    bannerEl.hidden = !msg;
  }
  function clearBanner() { bannerEl.hidden = true; bannerEl.textContent = ''; }

  // ================================================================
  // Session lifecycle
  // ================================================================
  function openSession() {
    invite.hidden = true;
    session.hidden = false;
    clearBanner();
    resultsEl.hidden = true;
    panelEl.innerHTML = '';
    countEl.textContent = '';
  }

  function resetToInvite() {
    // Abort whatever's in flight (the core bumps its generation too), so a
    // response that was already on its way over the network when "Start
    // again" was pressed can never land afterwards. The remembered dress
    // goes too: starting again means starting again.
    core.cancel();
    core.forget();
    invite.hidden = false;
    session.hidden = true;
    fileInput.value = '';
    preview.hidden = true;
    previewImg.removeAttribute('src');
    revokePreviewObjectUrl();
    stopReadingStatus();
    clearBanner();
    statusEl.textContent = '';
    chipsEl.innerHTML = '';
    chipsEl.classList.remove('bdm__chips--expanded');
    moreEl.innerHTML = '';
    moreEl.hidden = true;
    moreToggleBtn.hidden = true;
    moreToggleBtn.setAttribute('aria-expanded', 'false');
    editDetailsBtn.hidden = false;
    panelEl.innerHTML = '';
    resultsEl.hidden = true;
    state.rawTags = null;
    state.ranked = null;
    state.moreOpen = false;
    state.primaryExpanded = false;
    closePopover();
    dispatch('start_again', {});
  }

  // Revokes the local preview's object: URL, if one is outstanding — called
  // on load, on error, and whenever a new upload (or "Start again") is
  // about to replace it, so a superseded preview never leaks its blob.
  function revokePreviewObjectUrl() {
    if (state.previewObjectUrl) {
      core.revoke(state.previewObjectUrl);
      state.previewObjectUrl = null;
    }
  }

  function showLocalPreview(file) {
    preview.hidden = false;
    shimmer.hidden = false;
    revokePreviewObjectUrl();
    previewImg.onload = null;
    previewImg.onerror = null;
    var url = core.previewUrl(file);
    if (!url) { previewImg.removeAttribute('src'); return; } // HEIC: no local preview until it is converted
    state.previewObjectUrl = url;
    previewImg.onload = revokePreviewObjectUrl;
    previewImg.onerror = revokePreviewObjectUrl;
    previewImg.src = url;
  }

  function handleFile(file) {
    if (!file) return;
    if (state.unavailable) return; // the engine's already stood down — nothing to open
    openSession();
    if (file.size > core.maxBytes) {
      showBanner(safeI18n.t('js.rb.dm_state_too_large'));
      return;
    }
    dispatch('upload', { size: file.size, type: file.type || '' });
    showLocalPreview(file);
    processAndSend(file);
  }

  function enterDescribeMode(keepSessionOpen) {
    if (state.unavailable) return;
    if (!keepSessionOpen) openSession();
    dispatch('describe_open', {});
    setQueryTags({});
  }

  // Arrived from the Dress Finder with ?dress=1 and a remembered dress:
  // straight into results for it. No upload and no thumbnail — the preview
  // slot keeps its neutral colour, without the "reading" shimmer. The chips
  // and results draw once the engine has loaded (ensureEngineLoaded), then
  // the block scrolls itself into view.
  function openFromRecall() {
    var tags = state.recalled;
    state.recalled = null;
    if (!tags || state.unavailable) return;
    log('opening from a remembered dress');
    openSession();
    preview.hidden = false;
    shimmer.hidden = true;
    state.rawTags = tags;
    state.scrollWhenReady = true;
    ensureEngineLoaded();
  }

  // ================================================================
  // "Your dress" chips (profileDescription + a "+ Field" per unknown one)
  // ================================================================
  function setQueryTags(tags) {
    state.rawTags = tags || {};
    core.remember(state.rawTags);
    renderChips();
    rankAndRender();
  }

  // Field grouping: index.chip_secondary (an array of field names), when
  // present, names everything that isn't primary — falls back to
  // DEFAULT_PRIMARY_FIELDS otherwise. Values named in index.chip_hide_values
  // (or DEFAULT_HIDE_VALUES) are demoted out of the primary row even when
  // their field is primary — a "negative" answer (no buttons, no train) is
  // rarely the interesting thing about a dress.
  function primaryFields() {
    var secondary = state.index && Array.isArray(state.index.chip_secondary)
      ? state.index.chip_secondary
      : null;
    if (!secondary) return DEFAULT_PRIMARY_FIELDS;
    var secondarySet = {};
    secondary.forEach(function (f) { secondarySet[f] = true; });
    return DRESS_FIELDS.filter(function (f) { return !secondarySet[f]; });
  }
  function isHiddenValue(field, value) {
    var table = (state.index && state.index.chip_hide_values) || DEFAULT_HIDE_VALUES;
    var hidden = table[field];
    return !!hidden && hidden.indexOf(value) > -1;
  }

  function renderChips() {
    chipsEl.innerHTML = '';
    moreEl.innerHTML = '';
    if (!state.index || !state.rawTags) {
      moreToggleBtn.hidden = true;
      return;
    }
    var known = core.describe(state.rawTags);
    var knownMap = {};
    var knownExtras = [];
    known.forEach(function (d) {
      if (d.field === 'extras') knownExtras.push(d);
      else knownMap[d.field] = d.label;
    });

    var primary = primaryFields();
    var primarySet = {};
    primary.forEach(function (f) { primarySet[f] = true; });

    primary.forEach(function (field) {
      if (!Object.prototype.hasOwnProperty.call(knownMap, field)) {
        chipsEl.appendChild(buildChip(field, null, false));
        return;
      }
      var target = isHiddenValue(field, state.rawTags[field]) ? moreEl : chipsEl;
      target.appendChild(buildChip(field, knownMap[field], true));
    });

    DRESS_FIELDS.forEach(function (field) {
      if (primarySet[field]) return;
      if (Object.prototype.hasOwnProperty.call(knownMap, field)) {
        moreEl.appendChild(buildChip(field, knownMap[field], true));
      } else {
        moreEl.appendChild(buildChip(field, null, false));
      }
    });
    knownExtras.forEach(function (d) { moreEl.appendChild(buildChip('extras', d.label, true)); });
    moreEl.appendChild(buildChip('extras', null, false));

    moreToggleBtn.hidden = false;
    moreToggleBtn.setAttribute('aria-expanded', state.moreOpen ? 'true' : 'false');
    moreEl.hidden = !state.moreOpen;
    if (state.primaryExpanded) chipsEl.classList.add('bdm__chips--expanded');
    editDetailsBtn.hidden = state.primaryExpanded;
  }

  function buildChip(field, label, known) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = known ? 'bdm__chip' : 'bdm__chip bdm__chip--unknown';
    b.setAttribute('data-field', field);
    var fLabel = FIELD_LABEL[field] || field;
    if (known) {
      b.textContent = label;
      b.setAttribute('aria-label', safeI18n.t('js.rb.dm_edit_field_aria', { field: fLabel }));
    } else {
      b.textContent = safeI18n.t('js.rb.dm_add_field', { field: fLabel });
    }
    b.addEventListener('click', function () { openFieldEditor(field, b); });
    return b;
  }

  function focusFieldChip(field) {
    var selector = '[data-field="' + field + '"]';
    var el = chipsEl.querySelector(selector) || moreEl.querySelector(selector);
    if (el) el.focus();
  }

  function onTagsEdited(field, keepFocus) {
    core.remember(state.rawTags);
    renderChips();
    rankAndRender();
    if (!keepFocus) focusFieldChip(field);
    dispatch('chip_change', { field: field });
  }

  // ================================================================
  // Field editor popover (tap a chip, or describe mode)
  // ================================================================
  function popKeydown(e) {
    if (e.key === 'Escape') {
      var f = state.activeField;
      closePopover();
      focusFieldChip(f);
      return;
    }
    if (e.key === 'Tab') {
      var focusables = popEl.querySelectorAll('button');
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  function popOutsideClick(e) {
    if (popEl.hidden) return;
    if (!popEl.contains(e.target)) closePopover();
  }
  function closePopover() {
    popEl.hidden = true;
    popEl.innerHTML = '';
    state.activeField = null;
    document.removeEventListener('keydown', popKeydown);
    document.removeEventListener('pointerdown', popOutsideClick, true);
  }
  function positionPopover(anchorEl) {
    var r = anchorEl.getBoundingClientRect();
    var top = r.bottom + window.scrollY + 8;
    var left = r.left + window.scrollX;
    var maxLeft = window.scrollX + document.documentElement.clientWidth - 332;
    if (left > maxLeft) left = Math.max(window.scrollX + 12, maxLeft);
    popEl.style.top = top + 'px';
    popEl.style.left = left + 'px';
  }

  function openFieldEditor(field, anchorEl) {
    state.activeField = field;
    var values = (state.index && state.index.vocab && state.index.vocab[field]) || {};
    var isMulti = field === 'extras';
    var current = isMulti
      ? (Array.isArray(state.rawTags.extras) ? state.rawTags.extras.slice() : [])
      : state.rawTags[field];

    popEl.innerHTML = '';
    var head = document.createElement('div');
    head.className = 'bdm__pop-head';
    var title = document.createElement('p');
    title.className = 'bdm__pop-title';
    title.textContent = FIELD_LABEL[field] || field;
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'bdm__pop-close';
    close.setAttribute('aria-label', safeI18n.t('js.rb.dm_pop_close_aria'));
    close.textContent = '×';
    close.addEventListener('click', function () { closePopover(); focusFieldChip(field); });
    head.appendChild(title);
    head.appendChild(close);
    popEl.appendChild(head);

    var opts = document.createElement('div');
    opts.className = 'bdm__pop-opts';

    Object.keys(values).forEach(function (val) {
      var o = document.createElement('button');
      o.type = 'button';
      o.className = 'bdm__pop-opt';
      var active = isMulti ? current.indexOf(val) > -1 : current === val;
      if (active) o.className += ' bdm__pop-opt--on';
      o.textContent = values[val];
      o.addEventListener('click', function () {
        if (isMulti) {
          var idx = current.indexOf(val);
          if (idx > -1) { current.splice(idx, 1); o.classList.remove('bdm__pop-opt--on'); }
          else { current.push(val); o.classList.add('bdm__pop-opt--on'); }
          state.rawTags.extras = current.slice();
          onTagsEdited('extras', true);
        } else {
          state.rawTags[field] = val;
          closePopover();
          onTagsEdited(field);
        }
      });
      opts.appendChild(o);
    });

    var notSure = document.createElement('button');
    notSure.type = 'button';
    notSure.className = 'bdm__pop-opt bdm__pop-opt--notsure';
    notSure.textContent = safeI18n.t('js.rb.dm_not_sure');
    notSure.addEventListener('click', function () {
      if (isMulti) { state.rawTags.extras = []; } else { delete state.rawTags[field]; }
      closePopover();
      onTagsEdited(field);
    });
    opts.appendChild(notSure);

    popEl.appendChild(opts);
    popEl.hidden = false;
    positionPopover(anchorEl);
    var firstBtn = popEl.querySelector('button');
    if (firstBtn) firstBtn.focus();
    document.addEventListener('keydown', popKeydown);
    document.addEventListener('pointerdown', popOutsideClick, true);
  }

  // ================================================================
  // Ranking + results
  // ================================================================
  function knownFieldCount(normalised) {
    var n = 0;
    for (var k in normalised) {
      if (!Object.prototype.hasOwnProperty.call(normalised, k)) continue;
      if (k === 'extras') { if (normalised.extras && normalised.extras.length) n++; }
      else n++;
    }
    return n;
  }

  function rankAndRender() {
    if (!state.index || !state.rawTags) return;
    var normalised = core.normalise(state.rawTags);

    if (!knownFieldCount(normalised)) {
      resultsEl.hidden = true;
      panelEl.innerHTML = '';
      countEl.textContent = '';
      announceResults({ tags: Object.assign({}, state.rawTags), chips: [], results: [] });
      return;
    }

    var ranked = core.rank(state.rawTags, {});
    var weak = false;
    if (!ranked.length) {
      ranked = core.rank(state.rawTags, { includeWeak: true });
      weak = true;
    }
    state.ranked = ranked;

    resultsEl.hidden = false;
    panelEl.innerHTML = '';

    if (!ranked.length) {
      countEl.textContent = '';
      appendEmptyNotice(safeI18n.t('js.rb.dm_state_no_results'));
      announceResults({ tags: Object.assign({}, state.rawTags), chips: primaryChips(), results: [] });
      return;
    }
    if (weak) appendEmptyNotice(safeI18n.t('js.rb.dm_state_no_close_matches'));

    countEl.textContent = safeI18n.n('js.rb.dm_result_count', ranked.length, { n: ranked.length });
    renderView(ranked);
    // Weak-only matches are not matches (the block itself says "no close
    // matches yet"), so the hook reports none.
    var forHook = [];
    try { if (!weak) forHook = resultsForHook(ranked, normalised); } catch (e) { /* the hook never breaks the block */ }
    announceResults({ tags: Object.assign({}, state.rawTags), chips: primaryChips(), results: forHook });
  }

  // ---- archive hook helpers (see the header) ----
  // The chips this block shows first for a known dress: the primary fields,
  // minus "negative" answers (no train, no lace...) — same rule as renderChips().
  function primaryChips() {
    if (!state.index || !state.rawTags) return [];
    var byField = {};
    core.describe(state.rawTags).forEach(function (d) {
      if (d.field !== 'extras') byField[d.field] = d.label;
    });
    return primaryFields().filter(function (f) {
      return Object.prototype.hasOwnProperty.call(byField, f) && !isHiddenValue(f, state.rawTags[f]);
    }).map(function (f) { return { field: f, label: byField[f] }; });
  }
  // A bride's best photo for a card: her first portrait photo (a full-length
  // shot), else her first. The index carries no per-photo score.
  // Crop focus point (fp, marketing_131): [x, y] per cent across / down.
  // Returns an object-position value, or '' when fp is missing or malformed.
  function fpPos(p) {
    var f = p && p.fp;
    if (!f || !Array.isArray(f) || f.length !== 2) return '';
    var x = f[0], y = f[1];
    if (typeof x !== 'number' || typeof y !== 'number' || !isFinite(x) || !isFinite(y)) return '';
    if (x < 0 || x > 100 || y < 0 || y > 100) return '';
    return Math.round(x) + '% ' + Math.round(y) + '%';
  }
  function bestPhotoOf(bride) {
    var ps = (bride && bride.photos) || [];
    for (var i = 0; i < ps.length; i++) {
      if (ps[i] && ps[i].src && ps[i].h > ps[i].w) return ps[i];
    }
    return ps[0] || null;
  }
  function resultsForHook(ranked, normalised) {
    var weights = (state.index && state.index.weights) || {};
    function weightOf(f) { return typeof weights[f] === 'number' ? weights[f] : 1; }
    var out = [];
    ranked.forEach(function (item) {
      var bride = item.bride || {};
      var photo = bestPhotoOf(bride);
      if (!photo) return;
      var v = photo.veil != null ? veilFor(photo.veil) : null;
      var handle = v && /\/products\/([^/?#]+)/.exec(v.url || '');
      var href = bride.href || '';
      var hookPhoto = { u: photo.src, w: photo.w, h: photo.h };
      if (fpPos(photo)) hookPhoto.fp = photo.fp;
      out.push({
        id: bride.id,
        slug: href.indexOf('/pages/') === 0 ? href.slice(7) : '',
        href: href,
        band: item.band,
        score: Math.round(item.score * 100) / 100,
        matched: (item.shared || []).filter(function (f) {
          return f !== 'extras' && !isHiddenValue(f, normalised[f]);
        }).sort(function (a, b) { return weightOf(b) - weightOf(a); }),
        photo: hookPhoto,
        veil: v ? { h: handle ? handle[1] : '', t: v.name || '' } : null
      });
    });
    return out;
  }

  function appendEmptyNotice(text) {
    var p = document.createElement('p');
    p.className = 'bdm__empty';
    p.textContent = text;
    panelEl.appendChild(p);
  }

  function renderView(ranked) {
    var container = document.createElement('div');
    if (state.view === 'grouped') {
      var groups = core.groupByVeil(ranked, {});
      if (!groups.length) {
        appendEmptyNotice(safeI18n.t('js.rb.dm_state_no_results'));
        return;
      }
      groups.forEach(function (g) { container.appendChild(buildVeilRow(g)); });
    } else {
      container.className = 'bdm__panel--closest';
      var order = core.closestFirst(ranked, {});
      var grid = document.createElement('div');
      grid.className = 'bdm__cards';
      order.forEach(function (item) {
        var photo = item.bride.photos && item.bride.photos[0];
        if (!photo) return;
        grid.appendChild(buildCard(item, photo, null));
      });
      container.appendChild(grid);
    }
    panelEl.appendChild(container);
  }

  function buildVeilRow(g) {
    var wrap = document.createElement('div');
    wrap.className = 'bdm__veil';

    var head = document.createElement('div');
    head.className = 'bdm__veil-head';
    var h3 = document.createElement('h3');
    h3.className = 'bdm__veil-name';
    h3.textContent = (g.veil && g.veil.name) || '';
    head.appendChild(h3);

    var metaParts = [];
    if (g.veil && g.veil.lace) metaParts.push(g.veil.lace);
    if (g.veil && g.veil.fabric) metaParts.push(g.veil.fabric);
    if (metaParts.length) {
      var meta = document.createElement('p');
      meta.className = 'bdm__veil-meta';
      meta.textContent = metaParts.join(' · ');
      head.appendChild(meta);
    }
    if (g.veil && g.veil.url) {
      var shop = document.createElement('a');
      shop.className = 'bdm__veil-shop';
      shop.href = g.veil.url;
      shop.textContent = safeI18n.t('js.rb.dm_shop_the_veil');
      shop.addEventListener('click', function () { dispatch('shop_click', { veil: g.veilId, from: 'row' }); });
      head.appendChild(shop);
    }
    wrap.appendChild(head);

    var cards = document.createElement('div');
    cards.className = 'bdm__cards';
    g.brides.forEach(function (item) {
      var photo = item.bride.photos && item.bride.photos[0];
      if (!photo) return;
      cards.appendChild(buildCard(item, photo, g.veil));
    });
    wrap.appendChild(cards);
    return wrap;
  }

  // Most brides have no first name on file (real index: roughly two in
  // three) — "Wearing 'Adela'" reads fine on its own. The remaining edge
  // case, no resolvable veil name either, would otherwise print an empty
  // "Wearing ''" — falls back to a plain, still-true caption instead.
  function captionFor(name, veilName) {
    if (!veilName) return safeI18n.t('js.rb.dm_caption_generic');
    return name
      ? safeI18n.t('js.rb.dm_caption_named', { name: name, veil: veilName })
      : safeI18n.t('js.rb.dm_caption_unnamed', { veil: veilName });
  }

  function buildCard(item, photo, veilMeta) {
    var veilName = (veilMeta && veilMeta.name) || veilNameForPhoto(photo);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'bdm__card-item';

    var ph = document.createElement('div');
    ph.className = 'bdm__card-photo';
    var img = document.createElement('img');
    img.loading = 'lazy';
    img.src = cdnUrl(photo.src, 480);
    var cardPos = fpPos(photo);
    if (cardPos) img.style.objectPosition = cardPos;
    img.alt = veilName
      ? safeI18n.t('js.rb.dm_photo_alt', { veil: veilName })
      : safeI18n.t('js.rb.dm_photo_alt_no_veil');
    ph.appendChild(img);

    var band = document.createElement('span');
    band.className = 'bdm__card-band bdm__card-band--' + item.band;
    band.textContent = BAND_LABEL[item.band] || '';
    ph.appendChild(band);
    btn.appendChild(ph);

    var cap = document.createElement('p');
    cap.className = 'bdm__card-caption';
    cap.textContent = captionFor(item.bride.name, veilName);
    btn.appendChild(cap);

    var tagsWrap = document.createElement('div');
    tagsWrap.className = 'bdm__card-tags';
    (item.shared || []).slice(0, 3).forEach(function (field) {
      if (field === 'extras') return;
      var tag = document.createElement('span');
      tag.className = 'bdm__card-tag';
      var lbl = (FIELD_LABEL[field] || field).toLowerCase();
      tag.textContent = safeI18n.t('js.rb.dm_same_field', { field: lbl });
      tagsWrap.appendChild(tag);
    });
    btn.appendChild(tagsWrap);

    btn.addEventListener('click', function () {
      state.lastCardEl = btn;
      dispatch('result_click', { bride: item.bride.id, band: item.band });
      openLightbox(item, veilMeta);
    });
    return btn;
  }

  // ================================================================
  // Lightbox (own small one; visual language borrowed from the page's)
  // ================================================================
  function paintLightboxSlide() {
    var p = state.lbList[state.lbIndex];
    var veilName = state.lbVeil ? state.lbVeil.name : veilNameForPhoto(p);
    lbImg.src = cdnUrl(p.src, 1200);
    lbImg.alt = veilName
      ? safeI18n.t('js.rb.dm_photo_alt', { veil: veilName })
      : safeI18n.t('js.rb.dm_photo_alt_no_veil');
    lbPrev.hidden = state.lbList.length < 2;
    lbNext.hidden = state.lbList.length < 2;

    lbLinks.innerHTML = '';
    if (state.lbBride && state.lbBride.href) {
      var w = document.createElement('a');
      w.href = state.lbBride.href;
      w.textContent = safeI18n.t('js.rb.dm_see_wedding');
      lbLinks.appendChild(w);
    }
    if (state.lbVeil && state.lbVeil.url) {
      var s = document.createElement('a');
      s.href = state.lbVeil.url;
      s.textContent = safeI18n.t('js.rb.dm_shop_named', { veil: state.lbVeil.name });
      s.addEventListener('click', function () { dispatch('shop_click', { veil: state.lbVeil.name, from: 'lightbox' }); });
      lbLinks.appendChild(s);
    }

    lbCredit.innerHTML = '';
    if (p.credit) {
      // Link the photographer name only for an http(s) site, matching the
      // Picture Desk credit-link rule (assets/britten-picture-desk.js).
      if (p.credit_url && /^https?:\/\//i.test(p.credit_url)) {
        var a = document.createElement('a');
        a.href = p.credit_url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer nofollow';
        a.textContent = p.credit;
        lbCredit.appendChild(a);
      } else {
        lbCredit.appendChild(document.createTextNode(p.credit));
      }
    }
  }

  function openLightbox(item, veilMeta) {
    var photos = (item.bride.photos && item.bride.photos.length) ? item.bride.photos : null;
    if (!photos) return;
    state.lbList = photos;
    state.lbIndex = 0;
    state.lbBride = item.bride;
    state.lbVeil = veilMeta || veilFor(photos[0].veil);
    lbTitle.textContent = item.bride.name || '';
    paintLightboxSlide();
    lb.hidden = false;
    lb.classList.add('bdm__lb--open');
    lbClose.focus();
    document.addEventListener('keydown', lbKeydown);
  }
  function closeLightbox() {
    lb.classList.remove('bdm__lb--open');
    lb.hidden = true;
    document.removeEventListener('keydown', lbKeydown);
    if (state.lastCardEl) state.lastCardEl.focus();
  }
  function lbStep(dir) {
    if (state.lbList.length < 2) return;
    state.lbIndex = (state.lbIndex + dir + state.lbList.length) % state.lbList.length;
    paintLightboxSlide();
  }
  // Only the currently-visible buttons/links — lbPrev/lbNext are [hidden]
  // (single-photo bride), and lbLinks' contents change slide to slide.
  function lbFocusables() {
    var all = lb.querySelectorAll('button, a[href]');
    var out = [];
    for (var i = 0; i < all.length; i++) {
      if (!all[i].hidden && all[i].offsetParent !== null) out.push(all[i]);
    }
    return out;
  }
  function lbKeydown(e) {
    if (e.key === 'Escape') { closeLightbox(); return; }
    if (e.key === 'ArrowLeft') { lbStep(-1); return; }
    if (e.key === 'ArrowRight') { lbStep(1); return; }
    if (e.key === 'Tab') {
      var focusables = lbFocusables();
      if (!focusables.length) return;
      var first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  // ================================================================
  // Wiring
  // ================================================================
  fileInput.addEventListener('change', function () {
    var f = fileInput.files && fileInput.files[0];
    if (f) handleFile(f);
  });
  drop.addEventListener('keydown', function (e) {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
  });
  ['dragenter', 'dragover'].forEach(function (evt) {
    drop.addEventListener(evt, function (e) { e.preventDefault(); drop.classList.add('bdm__drop--over'); });
  });
  ['dragleave', 'drop'].forEach(function (evt) {
    drop.addEventListener(evt, function (e) { e.preventDefault(); drop.classList.remove('bdm__drop--over'); });
  });
  drop.addEventListener('drop', function (e) {
    var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    if (f) handleFile(f);
  });

  describeOpenBtn.addEventListener('click', function () { enterDescribeMode(false); });
  startAgainBtn.addEventListener('click', resetToInvite);

  editDetailsBtn.addEventListener('click', function () {
    state.primaryExpanded = true;
    chipsEl.classList.add('bdm__chips--expanded');
    editDetailsBtn.hidden = true;
  });
  moreToggleBtn.addEventListener('click', function () {
    state.moreOpen = !state.moreOpen;
    moreEl.hidden = !state.moreOpen;
    moreToggleBtn.setAttribute('aria-expanded', state.moreOpen ? 'true' : 'false');
  });

  viewGroupedBtn.addEventListener('click', function () {
    if (state.view === 'grouped') return;
    state.view = 'grouped';
    viewGroupedBtn.classList.add('bdm__view-btn--on');
    viewGroupedBtn.setAttribute('aria-pressed', 'true');
    viewClosestBtn.classList.remove('bdm__view-btn--on');
    viewClosestBtn.setAttribute('aria-pressed', 'false');
    rankAndRender();
  });
  viewClosestBtn.addEventListener('click', function () {
    if (state.view === 'closest') return;
    state.view = 'closest';
    viewClosestBtn.classList.add('bdm__view-btn--on');
    viewClosestBtn.setAttribute('aria-pressed', 'true');
    viewGroupedBtn.classList.remove('bdm__view-btn--on');
    viewGroupedBtn.setAttribute('aria-pressed', 'false');
    rankAndRender();
  });

  // Archive hook: open one field's editor from outside (see the header).
  root.addEventListener('bdm:edit', function (e) {
    var d = e && e.detail;
    if (!d || !d.field || !state.rawTags || state.unavailable) return;
    openFieldEditor(d.field, d.anchor || root);
  });

  lbClose.addEventListener('click', closeLightbox);
  lbPrev.addEventListener('click', function () { lbStep(-1); });
  lbNext.addEventListener('click', function () { lbStep(1); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleIndexCheck);
  } else {
    scheduleIndexCheck();
  }
  observeForEngine();
}());
